import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useApp } from '@/components/AppLayout';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { ClientDialog } from '@/components/ClientDialog';
import {
  clientApi,
  invoiceApi,
  quoteApi,
  paymentApi,
  type Client,
  type Invoice,
  type Quote,
  type Payment,
  type ClientOutstandingResponse,
} from '@/lib/api';
import { getInvoiceStatusConfig } from '@/lib/invoiceStatus';
import { getQuoteStatusConfig } from '@/lib/quoteStatus';
import { formatAmount } from '@/lib/currency';
import {
  User,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  FileText,
  Receipt,
  CreditCard,
  ArrowLeft,
  Pencil,
  Plus,
  ChevronDown,
  ExternalLink,
  Calendar,
  Clock,
  Check,
  X,
} from 'lucide-react';

export function ViewClient() {
  const { id } = useParams<{ id: string }>();
  const { nav } = useBusinessNav();
  const { currentBusiness, isViewOnly } = useApp();
  const businessId = currentBusiness?.id || '';

  // Data state
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<Client | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [outstandingInfo, setOutstandingInfo] = useState<ClientOutstandingResponse | null>(null);
  const [error, setError] = useState('');

  // UI state
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Load client data
  useEffect(() => {
    if (businessId && id) {
      loadClientData();
    }
  }, [businessId, id]);

  const loadClientData = async () => {
    if (!id) return;

    setLoading(true);

    // Load client details
    const clientRes = await clientApi.get(id);
    if (clientRes.error) {
      setError(clientRes.error);
      setLoading(false);
      return;
    }
    setClient(clientRes.data || null);

    // Load related data in parallel
    const [invoicesRes, quotesRes, paymentsRes, outstandingRes] = await Promise.all([
      invoiceApi.list(businessId, [id]),
      quoteApi.list(businessId, [id]),
      paymentApi.list(businessId, id),
      paymentApi.getClientOutstanding(id),
    ]);

    if (invoicesRes.data?.results) {
      // Sort by date and take recent
      setInvoices(invoicesRes.data.results.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ).slice(0, 10));
    }

    if (quotesRes.data?.results) {
      setQuotes(quotesRes.data.results.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ).slice(0, 10));
    }

    if (paymentsRes.data) {
      // Payments API returns array directly, not paginated
      const paymentsArray = Array.isArray(paymentsRes.data) ? paymentsRes.data : [];
      setPayments(paymentsArray.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ).slice(0, 10));
    }

    if (outstandingRes.data) {
      setOutstandingInfo(outstandingRes.data);
    }

    setLoading(false);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString.includes('T') ? dateString : dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) return <div className="fixed inset-0 z-50 bg-background" />;

  if (!client || error) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
          <div className="h-6 w-px bg-border" />
          <span className="font-medium text-sm">{currentBusiness?.name}</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || 'Client not found'}</p>
            <Button variant="outline" onClick={() => nav('/clients')}>
              Back to Clients
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const totalOutstanding = outstandingInfo ? parseFloat(outstandingInfo.total_outstanding) : 0;
  const availableCredit = outstandingInfo ? parseFloat(outstandingInfo.available_credit) : 0;
  const accountBalance = outstandingInfo ? parseFloat(outstandingInfo.account_balance) : 0;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
              <button
                onClick={() => nav('/clients')}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                All Clients
              </button>
              <div className="flex-1" />
              {!isViewOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditDialogOpen(true)}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="max-w-3xl mx-auto space-y-6"
              >
                {/* Client Header */}
                <div className="flex items-start gap-4">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl truncate">{client.billing_name}</h1>
                    {client.company_name && (
                      <div className="flex items-center gap-2 text-muted-foreground mt-1">
                        <Building2 className="h-4 w-4" />
                        <span>{client.company_name}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="space-y-2"
                >
                  <Label className="text-muted-foreground">Contact Information</Label>
                  <div className="border rounded-lg divide-y bg-muted/30">
                    {client.primary_email && (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{client.primary_email}</span>
                        {client.emails.length > 1 && (
                          <Badge variant="secondary" className="text-xs">
                            +{client.emails.length - 1} more
                          </Badge>
                        )}
                      </div>
                    )}
                    {client.primary_phone?.number && (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{client.primary_phone.country_code} {client.primary_phone.number}</span>
                        {client.phones.length > 1 && (
                          <Badge variant="secondary" className="text-xs">
                            +{client.phones.length - 1} more
                          </Badge>
                        )}
                      </div>
                    )}
                    {client.address && (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{client.address}</span>
                      </div>
                    )}
                    {client.website && (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <a
                          href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {client.website}
                        </a>
                      </div>
                    )}
                    {!client.primary_email && !client.primary_phone?.number && !client.address && !client.website && (
                      <div className="px-4 py-3 text-muted-foreground text-sm">
                        No contact information added
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Recent Invoices */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-medium">Recent Invoices</Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => nav(`/invoices?client=${id}`)}
                      className="text-muted-foreground"
                    >
                      View All
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  </div>

                  {invoices.length > 0 ? (
                    <div className="border rounded-lg divide-y">
                      {invoices.slice(0, 5).map((invoice) => {
                        const statusConfig = getInvoiceStatusConfig(invoice.status, '');
                        return (
                          <div
                            key={invoice.id}
                            className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => nav(`/invoices/${invoice.id}`)}
                          >
                            <div className="flex items-center gap-3">
                              <div>
                                <div className="font-medium">Invoice #{invoice.invoice_number}</div>
                                <div className="text-sm text-muted-foreground flex items-center gap-2">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(invoice.invoice_date)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className={statusConfig.textColorClass}>
                                {statusConfig.label}
                              </Badge>
                              <span className="font-medium">{formatAmount(invoice.total_amount)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="border rounded-lg p-6 text-center text-muted-foreground">
                      No invoices yet
                    </div>
                  )}
                </motion.div>

                {/* Recent Quotes */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-medium">Recent Quotes</Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => nav(`/quotes?client=${id}`)}
                      className="text-muted-foreground"
                    >
                      View All
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  </div>

                  {quotes.length > 0 ? (
                    <div className="border rounded-lg divide-y">
                      {quotes.slice(0, 5).map((quote) => {
                        const statusConfig = getQuoteStatusConfig(quote.status, '');
                        return (
                          <div
                            key={quote.id}
                            className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => nav(`/quotes/${quote.id}`)}
                          >
                            <div className="flex items-center gap-3">
                              <div>
                                <div className="font-medium">Quote #{quote.quote_number}</div>
                                <div className="text-sm text-muted-foreground flex items-center gap-2">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(quote.quote_date)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className={statusConfig.textColorClass}>
                                {statusConfig.label}
                              </Badge>
                              <span className="font-medium">{formatAmount(quote.total_amount)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="border rounded-lg p-6 text-center text-muted-foreground">
                      No quotes yet
                    </div>
                  )}
                </motion.div>

                {/* Recent Payments */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-3"
                >
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                    <Label className="text-base font-medium">Recent Payments</Label>
                  </div>

                  {payments.length > 0 ? (
                    <div className="border rounded-lg divide-y">
                      {payments.slice(0, 5).map((payment) => {
                        const statusConfig = {
                          completed: {
                            bgClass: 'bg-green-500/10',
                            iconClass: 'text-green-600',
                            amountClass: 'text-green-600',
                            Icon: Check,
                            label: null,
                          },
                          pending: {
                            bgClass: 'bg-amber-500/10',
                            iconClass: 'text-amber-600',
                            amountClass: 'text-amber-600',
                            Icon: Clock,
                            label: 'Processing',
                          },
                          failed: {
                            bgClass: 'bg-red-500/10',
                            iconClass: 'text-red-600',
                            amountClass: 'text-red-600 line-through',
                            Icon: X,
                            label: 'Failed',
                          },
                        }[payment.status] || {
                          bgClass: 'bg-green-500/10',
                          iconClass: 'text-green-600',
                          amountClass: 'text-green-600',
                          Icon: Check,
                          label: null,
                        };
                        const StatusIcon = statusConfig.Icon;

                        return (
                          <div
                            key={payment.id}
                            className="flex items-center justify-between px-4 py-3"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full ${statusConfig.bgClass} flex items-center justify-center`}>
                                <StatusIcon className={`h-4 w-4 ${statusConfig.iconClass}`} />
                              </div>
                              <div>
                                <div className="font-medium capitalize flex items-center gap-2">
                                  {payment.payment_method.replace(/_/g, ' ')}
                                  {statusConfig.label && (
                                    <Badge variant="outline" className={statusConfig.iconClass}>
                                      {statusConfig.label}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground flex items-center gap-2">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(payment.payment_date)}
                                  {payment.reference && (
                                    <span className="text-xs">• {payment.reference}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <span className={`font-medium ${statusConfig.amountClass}`}>
                              {formatAmount(payment.amount)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="border rounded-lg p-6 text-center text-muted-foreground">
                      No payments yet
                    </div>
                  )}
                </motion.div>

                {/* Internal Notes */}
                {client.notes && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="space-y-2"
                  >
                    <Label className="text-muted-foreground">Internal Notes</Label>
                    <div className="text-sm bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 border border-amber-200 dark:border-amber-900 whitespace-pre-wrap">
                      {client.notes}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.div>

          {/* Right Sidebar */}
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', damping: 25 }}
            className="w-72 border-l bg-muted/30 flex flex-col flex-shrink-0"
          >
            {/* Account Balance Header */}
            <div className="px-5 py-4 border-b">
              <div className="text-sm text-muted-foreground">Account Balance</div>
              <div className={`text-2xl font-semibold ${accountBalance > 0 ? 'text-amber-600' : accountBalance < 0 ? 'text-green-600' : ''}`}>
                {accountBalance < 0 ? '+' : ''}{formatAmount(Math.abs(accountBalance))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-5 pt-4 space-y-5">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Summary</h2>

              {/* Outstanding Balance */}
              <div className="space-y-3">
                <Label className="text-muted-foreground">Outstanding</Label>
                <div className="p-3 border rounded-lg bg-background">
                  <div className={`text-lg font-semibold ${totalOutstanding > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    {formatAmount(totalOutstanding)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {outstandingInfo?.outstanding_invoices.length || 0} unpaid invoice{(outstandingInfo?.outstanding_invoices.length || 0) !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Available Credit */}
              {availableCredit > 0 && (
                <div className="space-y-3">
                  <Label className="text-muted-foreground">Available Credit</Label>
                  <div className="p-3 border rounded-lg bg-background">
                    <div className="text-lg font-semibold text-green-600">
                      +{formatAmount(availableCredit)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      From credit memos
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="space-y-3">
                <Label className="text-muted-foreground">Activity</Label>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Invoices</span>
                    <span>{invoices.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Quotes</span>
                    <span>{quotes.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Payments</span>
                    <span>{payments.length}</span>
                  </div>
                </div>
              </div>

              {/* Payment Terms */}
              {client.payment_terms && (
                <div className="space-y-3">
                  <Label className="text-muted-foreground">Default Payment Terms</Label>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="capitalize">{client.payment_terms.replace(/_/g, ' ')}</span>
                  </div>
                </div>
              )}

              {/* Client Since */}
              <div className="space-y-3">
                <Label className="text-muted-foreground">Client Since</Label>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{formatDate(client.created_at)}</span>
                </div>
              </div>
            </div>

            {/* Fixed action buttons at bottom */}
            <div className="p-4 border-t bg-background space-y-3">
              {!isViewOnly && (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="w-full">
                        <Plus className="h-4 w-4 mr-1" />
                        Create New
                        <ChevronDown className="h-4 w-4 ml-auto" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => nav(`/invoices/create?clientId=${client.id}`)}>
                        <FileText className="h-4 w-4 mr-2" />
                        New Invoice
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => nav(`/quotes/create?clientId=${client.id}`)}>
                        <Receipt className="h-4 w-4 mr-2" />
                        New Quote
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {totalOutstanding > 0 && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => nav(`/payments/new?client_id=${client.id}`)}
                    >
                      <CreditCard className="h-4 w-4 mr-1" />
                      Record Payment
                    </Button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Edit Client Dialog */}
      {currentBusiness && (
        <ClientDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          businessId={currentBusiness.id}
          client={client}
          onSaved={() => {
            loadClientData();
            setEditDialogOpen(false);
          }}
        />
      )}
    </>
  );
}
